import { z } from "zod";
import { AD_ASPECT_RATIOS } from "./aspectRatios";
import { MAX_AD_PRODUCT_IMAGE_URLS, MAX_AD_REFERENCES } from "./ad-generation-request";

/** Limits enforced by the existing Ads Express entry points.  These are not
 * aspirational MCP quotas: callers can use them to bound a native request. */
export const MCP_STATIC_ADS_LIMITS = {
  maxAdsExpressTemplates: 50,
  maxKillCrewTemplates: 200,
  maxCreativeIterationSources: 10,
  maxAspectRatios: AD_ASPECT_RATIOS.length,
  maxReferenceImages: MAX_AD_REFERENCES,
  maxProductImageUrls: MAX_AD_PRODUCT_IMAGE_URLS,
  maxActiveRunsPerUserBrandVariant: 4,
  maxCreativeAngleCandidates: 20,
  imageCreativeIterationVariantsPerSource: 7,
  videoCreativeIterationVariantsPerSource: 4,
  /** Decoded bytes. Base64 transport overhead is deliberately not included. */
  maxReferenceImageBytes: 10 * 1024 * 1024,
} as const;

const id = z.string().trim().min(1).max(200);
const text = (max: number) => z.string().trim().min(1).max(max);
const nullableText = (max: number) => text(max).nullable();
const aspectRatio = z.enum(AD_ASPECT_RATIOS as unknown as [string, ...string[]]);

/** A URL that is safe to *submit* for server-side image import.  The importer
 * must still resolve DNS and re-check every redirect before fetching it. */
export function isSafeExternalImageUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase().replace(/^\[|\]$/g, "");
    if (url.protocol !== "https:" || url.username || url.password || !host) return false;
    if (host === "localhost" || host.endsWith(".localhost") || host.endsWith(".local")) return false;
    if (/^(0|127)(\.\d{1,3}){3}$/.test(host) || /^169\.254\.\d{1,3}\.\d{1,3}$/.test(host)) return false;
    if (/^10(\.\d{1,3}){3}$/.test(host) || /^192\.168(\.\d{1,3}){2}$/.test(host)) return false;
    if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(host)) return false;
    return host !== "::1" && !host.startsWith("fc") && !host.startsWith("fd") && !host.startsWith("fe80:");
  } catch {
    return false;
  }
}

export const mcpStaticAdsExternalImageUrlSchema = z.string().trim().max(2048).refine(isSafeExternalImageUrl, {
  message: "image URL must be a public HTTPS URL without credentials",
});

/** A portable image attachment. `bytesBase64` is standard base64 (not a data
 * URL) and is bounded before it is decoded. The receiving service must sniff
 * the decoded bytes; a caller-supplied MIME type is never authoritative. */
const base64ImageBytes = z.string().min(4)
  .max(Math.ceil(MCP_STATIC_ADS_LIMITS.maxReferenceImageBytes * 4 / 3) + 4)
  .refine((value) => /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value), {
    message: "bytesBase64 must be canonical base64 without a data URL prefix",
  })
  .refine((value) => {
    const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
    return (value.length / 4) * 3 - padding <= MCP_STATIC_ADS_LIMITS.maxReferenceImageBytes;
  }, { message: `decoded image must be at most ${MCP_STATIC_ADS_LIMITS.maxReferenceImageBytes} bytes` });

export const mcpStaticAdsImageAttachmentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("base64"), bytesBase64: base64ImageBytes }).strict(),
  // URL attachment is a fallback for MCP clients that cannot transmit bytes.
  // It is fetched only by the DNS- and redirect-guarded service boundary.
  z.object({ kind: z.literal("url"), url: mcpStaticAdsExternalImageUrlSchema }).strict(),
]);
export const mcpStaticAdsWorkflowSchema = z.enum(["ads_express", "creative_iterations"]);
/** `mode` is retained as the native service's term for a workflow family. */
export const mcpStaticAdsModeSchema = mcpStaticAdsWorkflowSchema;
export const mcpStaticAdsStatusSchema = z.enum(["queued", "running", "succeeded", "partial", "failed", "cancelled"]);
export const mcpStaticAdsTemplateIdSchema = id;
export const mcpStaticAdsExternalPlatformSchema = z.enum(["meta"]);
export const mcpStaticAdsExternalMediaTypeSchema = z.enum(["image", "video", "carousel"]);

// Read-only Creative Family inspection contracts. These intentionally keep
// PersonaOS node/generated-ad IDs separate from provider account/ad/creative
// identifiers; callers must never use a display name as lineage evidence.
const lineagePage = z.object({
  cursor: z.string().trim().min(1).max(200).optional(),
  limit: z.number().int().min(1).max(50).default(25),
}).strict();
export const mcpCreativeLineageInspectInputSchema = z.object({
  brandId: id, generatedAdId: id,
  maxDepth: z.number().int().min(1).max(32).default(32),
}).strict();
export const mcpCreativeLineageDescendantsInputSchema = z.object({
  brandId: id, generatedAdId: id,
  maxDepth: z.number().int().min(1).max(32).default(32),
}).merge(lineagePage);
export const mcpCreativeLineageLaunchesInputSchema = z.object({
  brandId: id, generatedAdId: id,
  maxDepth: z.number().int().min(1).max(32).default(32),
}).merge(lineagePage);
export const mcpCreativeLineagePerformanceInputSchema = z.object({
  brandId: id, generatedAdId: id,
  dateRange: z.object({ startDate: z.string().date(), endDate: z.string().date() }).strict(),
  maxDepth: z.number().int().min(1).max(32).default(32),
}).strict().superRefine((value, ctx) => {
  if (value.dateRange.endDate < value.dateRange.startDate) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dateRange", "endDate"], message: "endDate must be on or after startDate." });
  }
});

/** A machine-readable problem and recovery action, suitable for an MCP client
 * to show directly rather than attempting to infer a retry from prose. */
export const mcpStaticAdsValidationIssueSchema = z.object({
  path: z.array(z.union([z.string(), z.number()])).min(1),
  code: text(100),
  expected: z.string().trim().max(500).nullable(),
  message: text(1000),
  recoveryAction: text(1000),
}).strict();

const externalMediaItem = z.object({
  ordinal: z.number().int().nonnegative(),
  platformMediaId: id.nullable().optional(),
  mediaType: z.enum(["image", "video"]),
  // A provider URL is evidence/retrieval input, never a successful import's
  // durable asset URL. The service must copy it before making a source usable.
  retrievalUrl: mcpStaticAdsExternalImageUrlSchema.nullable().optional(),
  attachment: mcpStaticAdsImageAttachmentSchema.optional(),
  retrievedAt: z.string().datetime().nullable().optional(),
  fidelity: z.enum(["full_resolution", "thumbnail_only", "unknown"]).default("unknown"),
}).strict();

export const mcpStaticAdsResolveOrImportInputSchema = z.object({
  brandId: id,
  workflow: mcpStaticAdsWorkflowSchema,
  platform: mcpStaticAdsExternalPlatformSchema,
  platformAccountId: id,
  platformAdId: id,
  platformCreativeId: id.nullable().optional(),
  mediaType: mcpStaticAdsExternalMediaTypeSchema.optional(),
  // Public callers identify an authorized provider record. PersonaOS derives
  // media, naming, provenance, and metrics from Windsor server-side.
  dateRange: z.object({ startDate: z.string().date(), endDate: z.string().date() }).strict().optional(),
  idempotencyKey: text(160),
}).strict().superRefine((value, ctx) => {
  if (value.dateRange && value.dateRange.endDate < value.dateRange.startDate) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["dateRange", "endDate"], message: "endDate must be on or after startDate." });
});

export const mcpStaticAdsResolveOrImportOutputSchema = z.object({
  outcome: z.enum(["resolved", "imported", "pending", "rejected"]),
  platform: mcpStaticAdsExternalPlatformSchema,
  platformAccountId: id,
  platformAdId: id,
  platformCreativeId: id.nullable(),
  sourceAdId: id.nullable(),
  assetIds: z.array(id),
  operationId: id.nullable(),
  idempotencyKey: id,
  provenance: z.object({
    provider: text(100), retrievedAt: z.string().datetime(),
    durableMedia: z.boolean(), fidelity: z.enum(["full_resolution", "thumbnail_only", "mixed", "unknown"]),
  }).strict(),
  compatibility: z.object({
    workflow: mcpStaticAdsWorkflowSchema, supported: z.boolean(), code: nullableText(100),
    message: text(1000), recoveryAction: text(1000),
  }).strict(),
  validationIssues: z.array(mcpStaticAdsValidationIssueSchema).max(20),
}).strict();

export const mcpStaticAdsResolveInputSchema = z.object({
  brandId: id,
  productId: id.nullable().optional(),
  offerId: id.nullable().optional(),
  personaId: id.nullable().optional(),
  narrativeAngleId: id.nullable().optional(),
  creativeAngleId: id.nullable().optional(),
}).strict();
export const mcpStaticAdsResolveOutputSchema = z.object({
  brandId: id, productId: id.nullable(), offerId: id.nullable(), personaId: id.nullable(),
  narrativeAngleId: id.nullable(), creativeAngleId: id.nullable(),
  availableTemplateIds: z.array(id),
}).strict();

export const mcpStaticAdsCapabilityInputSchema = z.object({ brandId: id, workflow: mcpStaticAdsWorkflowSchema }).strict();
export const mcpStaticAdsCapabilityOutputSchema = z.object({
  workflow: mcpStaticAdsWorkflowSchema, available: z.boolean(), code: nullableText(100), reason: nullableText(500),
  limits: z.object({
    maxTemplates: z.number().int().positive(), maxAspectRatios: z.number().int().positive(),
    maxReferenceImages: z.number().int().nonnegative(), maxProductImageUrls: z.number().int().nonnegative(),
    maxActiveRunsPerUserBrandVariant: z.number().int().positive(),
  }).strict(),
}).strict();

const references = z.array(z.object({
  url: mcpStaticAdsExternalImageUrlSchema, note: z.string().trim().max(1000).default(""),
  purpose: z.enum(["auto", "environment", "product", "product_environment", "person_identity"]).default("auto"),
  identityConsent: z.boolean().optional(),
}).strict()).max(MAX_AD_REFERENCES);
export const mcpStaticAdsPrepareInputSchema = z.discriminatedUnion("workflow", [
  z.object({
    brandId: id, workflow: z.literal("ads_express"), templateIds: z.array(id).min(1).max(MCP_STATIC_ADS_LIMITS.maxAdsExpressTemplates),
    // These are required by the native standard Ads Express submission
    // contract. A caller may provide a custom concept instead of a stored
    // narrative angle, exactly as the browser submit path does.
    personaId: id, narrativeAngleId: id.nullable().optional(), customConcept: z.string().trim().min(1).max(2000).optional(),
    productId: id.nullable().optional(), offerId: id.nullable().optional(), creativeDirection: z.string().trim().max(2000).optional(),
    quantity: z.number().int().min(1).max(20).default(1),
    aspectRatios: z.array(aspectRatio).min(1).max(AD_ASPECT_RATIOS.length).optional(),
    productImageUrls: z.array(mcpStaticAdsExternalImageUrlSchema).max(MAX_AD_PRODUCT_IMAGE_URLS).optional(), references: references.optional(),
  }).strict(),
  z.object({
    brandId: id, workflow: z.literal("creative_iterations"), sourceAdIds: z.array(id).min(1).max(MCP_STATIC_ADS_LIMITS.maxCreativeIterationSources),
    mediaType: z.enum(["image", "video"]), aspectRatio: aspectRatio.optional(),
    creativeGuidanceByAdId: z.record(z.string().trim().max(2000)).optional(),
  }).strict(),
]).superRefine((value, ctx) => {
  if (value.workflow === "ads_express" && !value.productId && !value.offerId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["productId"], message: "Ads Express requires a product or offer" });
  }
  if (value.workflow === "ads_express" && !value.narrativeAngleId && !value.customConcept) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["narrativeAngleId"], message: "Ads Express requires a narrative angle or custom concept" });
  }
  if (value.workflow === "creative_iterations" && value.mediaType === "image" && !value.aspectRatio) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["aspectRatio"], message: "aspectRatio is required for image Creative Iterations" });
  }
});
export const mcpStaticAdsPreparedRunSchema = z.object({
  preparationId: id, version: id, workflow: mcpStaticAdsWorkflowSchema,
  templateIds: z.array(id).min(1).optional(), sourceAdIds: z.array(id).min(1).optional(),
  outputCount: z.number().int().positive(), requiresApproval: z.boolean(), expiresAt: z.string().datetime().nullable(),
  warnings: z.array(text(1000)).max(50),
}).strict();
export const mcpStaticAdsStartInputSchema = z.object({
  preparationId: id, version: id, idempotencyKey: text(160),
}).strict();
export const mcpStaticAdsStatusInputSchema = z.object({ operationId: id }).strict();
export const mcpStaticAdsResultSchema = z.object({
  id, templateId: id.nullable(), status: z.enum(["ready", "failed", "cancelled"]),
  imageUrl: z.string().url().max(2048).nullable(), error: nullableText(1000),
  aspectRatio: aspectRatio.nullable(),
}).strict();
export const mcpStaticAdsStatusOutputSchema = z.object({
  operationId: id, status: mcpStaticAdsStatusSchema, completed: z.number().int().nonnegative(),
  total: z.number().int().positive(), results: z.array(mcpStaticAdsResultSchema),
}).strict().refine((value) => value.completed <= value.total, { path: ["completed"], message: "completed cannot exceed total" });
export const mcpStaticAdsResultsInputSchema = z.object({ operationId: id, cursor: z.string().min(1).max(200).optional() }).strict();
export const mcpStaticAdsResultsOutputSchema = z.object({
  items: z.array(mcpStaticAdsResultSchema), nextCursor: z.string().nullable(),
}).strict();

export const mcpStaticAdsImageImportInputSchema = z.object({
  brandId: id, attachment: mcpStaticAdsImageAttachmentSchema, label: z.string().trim().max(200).optional(),
}).strict();
export const mcpStaticAdsTemplateExtractInputSchema = z.object({
  brandId: id, attachment: mcpStaticAdsImageAttachmentSchema, name: text(255).optional(), category: z.string().trim().max(100).optional(),
  generation: z.object({
    personaId: id, productId: id.nullable().optional(), offerId: id.nullable().optional(),
    narrativeAngleId: id, aspectRatios: z.array(aspectRatio).min(1).max(AD_ASPECT_RATIOS.length).optional(),
    creativeDirection: z.string().trim().max(2000).optional(),
  }).strict().optional(),
}).strict();

export const mcpStaticAdsTemplateSetInputSchema = z.discriminatedUnion("action", [
  z.object({ brandId: id, action: z.literal("list") }).strict(),
  z.object({ brandId: id, action: z.literal("create"), name: text(200), description: z.string().trim().max(1000).optional(), templateIds: z.array(id).default([]) }).strict(),
  z.object({ brandId: id, action: z.literal("get"), setId: id }).strict(),
  z.object({ brandId: id, action: z.literal("update"), setId: id, name: text(200).optional(), description: z.string().trim().max(1000).nullable().optional(), displayOrder: z.number().int().nonnegative().optional() }).strict(),
  z.object({ brandId: id, action: z.literal("delete"), setId: id }).strict(),
  z.object({ brandId: id, action: z.literal("add_members"), setId: id, templateIds: z.array(id).min(1) }).strict(),
  z.object({ brandId: id, action: z.literal("remove_members"), setId: id, templateIds: z.array(id).min(1) }).strict(),
  z.object({ brandId: id, action: z.literal("reorder_members"), setId: id, templateIds: z.array(id).min(1) }).strict(),
]);
export const mcpStaticAdsTemplateSetSchema = z.object({
  id, brandId: id, name: text(200), description: z.string().nullable(), templateIds: z.array(id), version: id,
}).strict();

// Clear aliases for adapters that use verb-first naming.
export const mcpStaticAdsImportImageInputSchema = mcpStaticAdsImageImportInputSchema;
export const mcpStaticAdsExtractTemplateInputSchema = mcpStaticAdsTemplateExtractInputSchema;
export const mcpStaticAdsRunStatusSchema = mcpStaticAdsStatusOutputSchema;

/** Standalone workflow catalog, advertised alongside the foundation catalog. */
export const MCP_STATIC_ADS_CATALOG = [
  {
    name: "personaos_creative_lineage_inspect",
    description: "Read a brand-scoped Creative Iterations family: explicit immediate parent, root, contributing sources, ancestry, unknown states, and separately identified PersonaOS and exact Meta IDs.",
    operation: "lineage_inspect", workflows: ["creative_iterations"], effect: "none",
    annotations: { readOnlyHint: true, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_creative_lineage_descendants",
    description: "List paginated, brand-scoped descendants of a Creative Iterations creative from persisted lineage edges, preserving archived references and explicit unknown or truncation states.",
    operation: "lineage_descendants", workflows: ["creative_iterations"], effect: "none",
    annotations: { readOnlyHint: true, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_creative_lineage_launches",
    description: "List paginated exact Meta launches and verified external links for a Creative Iterations creative; PersonaOS creative IDs and Meta account, campaign, ad set, ad, and creative IDs remain distinct.",
    operation: "lineage_launches", workflows: ["creative_iterations"], effect: "none",
    annotations: { readOnlyHint: true, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_creative_lineage_performance",
    description: "Compare a Creative Iterations root and descendants over an explicit date range using only observed exact-launch or verified-external Meta performance; individual launches remain itemized and exact external ads are deduplicated.",
    operation: "lineage_performance", workflows: ["creative_iterations"], effect: "none",
    annotations: { readOnlyHint: true, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_static_ads_resolve",
    description: "Resolve authoritative brand, product, offer, persona, narrative-angle, creative-angle, source-ad, and template IDs before using standalone Ads Express or Creative Iterations.",
    operation: "resolve", workflows: ["ads_express", "creative_iterations"], effect: "none",
    annotations: { readOnlyHint: true, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_static_ads_capability",
    description: "Check whether standalone Ads Express or Creative Iterations is available for a brand and return the native workflow limits before preparing a run.",
    operation: "capability", workflows: ["ads_express", "creative_iterations"], effect: "none",
    annotations: { readOnlyHint: true, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_static_ads_resolve_or_import",
    description: "Resolve exact authorized platform/account/ad/creative lineage to its PersonaOS source, or durably import a genuinely external ad for Ads Express or Creative Iterations with ordered media, provenance, performance evidence, workflow compatibility, and an idempotency key. Platform IDs, PersonaOS asset IDs, and source-ad IDs are returned separately.",
    operation: "resolve_or_import", workflows: ["ads_express", "creative_iterations"], effect: "storage_write",
    annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: true },
  },
  {
    name: "personaos_static_ads_prepare",
    description: "Prepare and validate a standalone Ads Express generation or Creative Iterations run. For Creative Iterations set workflow to creative_iterations and provide sourceAdIds plus mediaType.",
    operation: "prepare", workflows: ["ads_express", "creative_iterations"], effect: "none",
    annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_static_ads_start",
    description: "Start a previously prepared standalone Ads Express or Creative Iterations run with its preparation ID, immutable version, and an idempotency key.",
    operation: "start", workflows: ["ads_express", "creative_iterations"], effect: "generation",
    annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_static_ads_status",
    description: "Read durable queued, running, succeeded, partial, failed, or cancelled status for a standalone Ads Express or Creative Iterations operation.",
    operation: "status", workflows: ["ads_express", "creative_iterations"], effect: "none",
    annotations: { readOnlyHint: true, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_static_ads_results",
    description: "Read paginated generated outputs and errors from a standalone Ads Express or Creative Iterations operation.",
    operation: "results", workflows: ["ads_express", "creative_iterations"], effect: "none",
    annotations: { readOnlyHint: true, destructiveHint: false, openWorldHint: false },
  },
  {
    name: "personaos_static_ads_import_image",
    description: "Import a validated public HTTPS or base64 image into the brand media library for standalone Ads Express or Creative Iterations workflows.",
    operation: "image_import", workflows: ["ads_express", "creative_iterations"], effect: "storage_write",
    annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: true },
  },
  {
    name: "personaos_static_ads_extract_template",
    description: "Analyze an attached static ad and save it as a reusable brand template for standalone Ads Express and Creative Iterations.",
    operation: "template_extract", workflows: ["ads_express", "creative_iterations"], effect: "template_write",
    annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: true },
  },
  {
    name: "personaos_static_ads_template_set",
    description: "List, create, read, rename, delete, and reorder brand-owned static-template sets used by standalone Ads Express and Creative Iterations.",
    operation: "template_set_crud_membership", workflows: ["ads_express", "creative_iterations"], effect: "template_set_write",
    annotations: { readOnlyHint: false, destructiveHint: true, openWorldHint: false },
  },
] as const;

export type McpStaticAdsPrepareInput = z.infer<typeof mcpStaticAdsPrepareInputSchema>;
export type McpStaticAdsTemplateSetInput = z.infer<typeof mcpStaticAdsTemplateSetInputSchema>;
export type McpStaticAdsResolveOrImportInput = z.infer<typeof mcpStaticAdsResolveOrImportInputSchema>;